import React, {useLayoutEffect, useRef, useState} from 'react';
import {useTranslation} from 'next-i18next';
import {
	CircularProgress,
	Modal,
	ModalContent,
	ModalOverlay,
	SearchInput,
	useBreakpointValue,
	Center,
	ModalBody,
	ModalHeader,
	Heading,
	ModalCloseButton,
} from '@tempo/core';
import SearchMode from '@deezer/next/shared/utils/SearchMode';
import SearchResults from './SearchResults';
import type {SearchAllResult} from '@deezer/next/services/api/Starmania/requests/search/types';
import useGlobalSearch from '@deezer/next/shared/hooks/useGlobalSearch';
import {useActiveArtist} from '@deezer/next/contexts/main/hooks';

interface SearchModalProps {
	isVisible: boolean;
	onClose: () => void;
	searchMode: SearchMode;
	searchPlaceholder: string;
}

const SearchModal = ({
	isVisible,
	onClose,
	searchMode,
	searchPlaceholder,
}: SearchModalProps) => {
	const initialRef = useRef<HTMLInputElement>(null!);
	const {isSearching, results, error, search, setResults} =
		useGlobalSearch(searchMode);

	const [query, setQuery] = useState('');
	const {t} = useTranslation('d4c');
	const viewAllType: keyof SearchAllResult['data']['result'] | undefined =
		searchMode === SearchMode.editArtist ? 'artists' : void 0;

	const isServerError = Boolean(error);
	const activeArtistId = useActiveArtist();

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const query = e.target.value;

		setQuery(query);

		if (query) {
			if (activeArtistId) {
				search(query, {artistId: activeArtistId});
			} else {
				search(query);
			}
		}
	};

	const modalSize = useBreakpointValue({base: 'full', sm: '3xl', md: '5xl'});

	const handleClose = () => {
		setResults(undefined);
		setQuery('');
		onClose();
	};

	useLayoutEffect(() => {
		(() => {
			setResults(undefined);
			setQuery('');
		})();
	}, [isVisible, setResults]);

	return (
		<Modal
			isOpen={isVisible}
			onClose={handleClose}
			size={modalSize}
			scrollBehavior="inside"
			initialFocusRef={initialRef}
		>
			<ModalOverlay />
			<ModalContent pb="spacing.xs">
				<ModalCloseButton display={{base: 'flex', sm: 'none'}} />
				<ModalHeader mt={{base: 'spacing.2xl', sm: 0}}>
					<SearchInput
						ref={initialRef}
						data-testid="global-search-input"
						value={query}
						placeholder={searchPlaceholder}
						onChange={onChange}
					/>
				</ModalHeader>

				{(isSearching || isServerError || (!isSearching && results)) && (
					<ModalBody px="spacing.l" textAlign="left">
						{isServerError && (
							<Center p={8}>
								<Heading
									color="text.feedback.error.default"
									variant="title.section"
								>
									{t('errormessage_text_anunexpectederroroccurred_d4c-web')}
								</Heading>
							</Center>
						)}

						{isSearching && !isServerError && (
							<Center p={8}>
								{/*TODO unify spinner*/}
								<CircularProgress
									size={16}
									thickness="12px"
									isIndeterminate={true}
								/>
							</Center>
						)}

						{!isSearching && results && !isServerError && (
							<SearchResults
								data={results}
								searchMode={searchMode}
								typeKey={viewAllType}
							/>
						)}
					</ModalBody>
				)}
			</ModalContent>
		</Modal>
	);
};

export default SearchModal;
